'use client';

import { AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

interface ErrorBoundaryFallbackProps {
	error: Error & { digest?: string };
	reset: () => void;
	title?: string;
	description?: string;
	homeHref?: string;
}

export default function ErrorBoundaryFallback({
	error,
	reset,
	title = 'Something went wrong',
	description = 'We hit an unexpected error loading this page. Our team has been notified.',
	homeHref = '/',
}: ErrorBoundaryFallbackProps) {
	return (
		<div className="flex min-h-[50vh] items-center justify-center p-4">
			<Card className="w-full max-w-md">
				<CardHeader className="items-center text-center">
					<AlertTriangle className="mb-2 h-10 w-10 text-destructive" />
					<CardTitle className="text-xl">{title}</CardTitle>
				</CardHeader>
				<CardContent className="text-center">
					<p className="text-sm text-muted-foreground">{description}</p>
					{error.digest && <p className="mt-3 font-mono text-xs text-muted-foreground/60">ref: {error.digest}</p>}
				</CardContent>
				<CardFooter className="justify-center gap-3">
					<Button variant="outline" onClick={reset} data-pendo="error-boundary:retry">
						Try again
					</Button>
					<Button asChild>
						<a href={homeHref} data-pendo="error-boundary:go-home">
							Go home
						</a>
					</Button>
				</CardFooter>
			</Card>
		</div>
	);
}
